/**
 * Workshop version of TextFrame.
 *
 * Props-based, no React Flow or store dependencies.
 */

import { Icons } from "@/components/ui/icons";
import { FrameHeader } from "@/workshop/components/shapes/shared/frame-header";
import { FrameFooter } from "@/workshop/components/shapes/shared/frame-footer";
import { statusColor } from "@/workshop/components/shapes/shared/frame-utils";

interface TextFrameProps {
  title: string;
  text: string;
  status: "idle" | "streaming" | "error" | "done";
  agentName?: string;
  onCopy?: () => void;
  onResizeStart?: React.MouseEventHandler;
}

export function TextFrame({
  title,
  text,
  status,
  agentName,
  onCopy,
  onResizeStart,
}: TextFrameProps) {
  return (
    <div
      className="denker-glass-frame flex flex-col rounded-frame overflow-hidden"
      data-testid="text-frame"
    >
      <FrameHeader
        title={title}
        icon={Icons.Type}
        accentColor="bg-frame-text"
        agentName={agentName}
        statusColor={statusColor(status)}
        data-testid="text-frame-header"
      />

      <div className="scrollbar-none flex-1 overflow-auto px-3 py-2">
        <div className="text-secondary text-canvas-base leading-relaxed">
          {text ? (
            <div className="whitespace-pre-wrap break-words">{text}</div>
          ) : (
            <p className="italic text-muted">No content yet</p>
          )}
          {status === "streaming" && (
            <span
              className="ml-0.5 inline-block h-3.5 w-[2px] animate-pulse bg-accent"
              aria-label="Streaming cursor"
            />
          )}
        </div>
      </div>

      <FrameFooter onCopy={onCopy} onResizeStart={onResizeStart} data-testid="text-frame-footer" />
    </div>
  );
}
